import type { AttachmentFileList } from 'owa-attachment-file-types';
import { lazyLogAttachOrShareDatapoint } from 'owa-attachment-filepicker';
import type { CreateAttachmentsProperties } from 'owa-attachment-well-data';
import { createAttachments } from 'owa-mail-compose-command';
import type { ComposeViewState } from 'owa-mail-compose-store';
import type AttachmentDataProviderType from 'owa-service/lib/contract/AttachmentDataProviderType';
import { mailStore } from 'owa-mail-store';
import { SmartDocOption } from 'owa-mail-smart-pill-features-types/lib/schema/SmartDocOption';
import { logUsage } from 'owa-analytics';
import { lazyInsertInlineImages } from 'owa-editor-inlineimage-plugin';
import {
    lazyLogEntitiesFromFilePicker,
    lazyGetAttachmentSuggestionsTraceId,
} from 'owa-attachment-suggestions';

// This function assumes the caller is an AttachmentFilePicker for logging purposes.
// Pass in an action source if calling from elsewhere.
export async function aFPCreateAttachmentHandler(
    files: AttachmentFileList,
    viewState: ComposeViewState,
    properties: CreateAttachmentsProperties,
    dataProvider: AttachmentDataProviderType
): Promise<void> {
    // Set fromSmartDoc parameter in properties
    const isAddedFromSmartDoc: boolean =
        viewState.smartPillViewState.smartDocOption != SmartDocOption.None;
    properties.isAddedFromSmartDoc = isAddedFromSmartDoc;

    files = await createAttachments(
        files,
        viewState,
        properties,
        isAddedFromSmartDoc
            ? () => logSmartDocAttachmentsAdded(viewState)
            : null /* onAllAttachmentsProcessed */,
        null /* onAttachmentCreatedCallback */,
        null /* onAttachmentCanceledOrFailedCallback */,
        null /* tryAgainCallback */
    );

    if (properties.isInline && files.length > 0) {
        const insertInlineImages = await lazyInsertInlineImages.import();
        insertInlineImages(files, viewState, viewState.inlineImageV2);
    }

    const logAttachOrShareDatapoint = await lazyLogAttachOrShareDatapoint.import();
    const getAttachmentSuggestionsTraceId = await lazyGetAttachmentSuggestionsTraceId.import();
    const traceId = getAttachmentSuggestionsTraceId(viewState.referenceItemId);
    logAttachOrShareDatapoint(
        {
            files: files,
            isInline: properties.isInline,
            shouldShare: properties.shouldShare,
            dataProviderType: dataProvider,
        },
        traceId
    );

    lazyLogEntitiesFromFilePicker.importAndExecute(
        files,
        viewState.referenceItemId,
        viewState.suggestedAttachmentSessionId
    );
}

function logSmartDocAttachmentsAdded(viewState: ComposeViewState) {
    const referenceItem = mailStore.items.get(viewState.referenceItemId?.Id);

    let extractionId: string = '';
    if (referenceItem?.SmartDocData?.EntityId) {
        extractionId = referenceItem.SmartDocData.EntityId;
    }

    logUsage(
        'SmartDocAttachmentsAdded',
        { smartDocExtractionId: extractionId },
        { isCore: true, excludeFromKusto: true }
    );

    // Reset the SmartDocOption as user may add more attachments in the compose form, which should
    // be logged as "not fromSmartDoc".
    viewState.smartPillViewState.smartDocOption = SmartDocOption.None;
}
